import { soilDeficitZoneColor, type TSoilDeficitZone } from 'App/dashboards/_utils/soilDeficit'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { colors } from 'settings/colors'

// Match the Map's AWC chart y-range (getAwcChartMinMax) so the deficit bands carry the same context:
// the full band spectrum is visible and the line sits in its actual zone.
const SOFT_AWC_MIN = -10
const HARD_AWC_MIN = -50
const SOFT_AWC_MAX = 150
const HARD_AWC_MAX = 175
const MODERATE_DEFICIT_THRESHOLD = 0.7
const clamp = (value: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, value))

// Y-axis gridline/label values (AWC %); the visible range is always at least [-10, 150], so these all
// fall inside it. Exported so the modal can label its own axis against the very same scale.
export const AWC_Y_TICKS = [150, 100, 50, 0]

const Y_TICKS = [...AWC_Y_TICKS].reverse()

// The chart's visible AWC range for a series. Exported alongside the component because the modal draws
// its axis labels in the DOM rather than in the SVG — deriving both from this keeps them in lockstep
// instead of two copies of the same clamping rules drifting apart.
export const getAwcChartDomain = (values: (number | null)[]): { yMin: number; yMax: number } => {
  const numbers = values.filter((value) => value !== null)

  if (numbers.length === 0) return { yMin: SOFT_AWC_MIN, yMax: SOFT_AWC_MAX }

  return {
    yMin: Math.max(Math.min(...numbers, SOFT_AWC_MIN), HARD_AWC_MIN),
    yMax: Math.min(Math.max(...numbers, SOFT_AWC_MAX), HARD_AWC_MAX),
  }
}

// At most this many date labels on the x-axis, thinned evenly so they don't crowd.
const MAX_X_LABELS = 6

// A lightweight inline-SVG AWC sparkline: the root-zone-average soil-moisture line over the deficit-zone
// colour bands (extreme/high/moderate/ideal/saturated), MAD-relative — same banding as the "Current"
// chip and the Map's "Available Water Content" chart. The water-events overlay is intentionally left out.
// In the tiny table cell it renders bare (no axes); the expanded modal passes `showAxes` + `dates` to get
// an x-axis (dates) and y-axis (AWC %).
export const RootZoneSparkline = ({
  values,
  dates,
  madValue = 70,
  width = 160,
  height = 32,
  showAxes = false,
}: {
  values: (number | null)[]
  dates?: string[]
  madValue?: number
  width?: number
  height?: number
  showAxes?: boolean
}) => {
  const points = values
    .map((value, index) => ({ value, index }))
    .filter((p): p is { value: number; index: number } => p.value !== null)

  if (points.length === 0) return <span css={{ color: colors.grey800 }}>{'-'}</span>

  // Axes need room for labels; the bare cell sparkline reserves nothing (margins collapse to 0, so the
  // chart maths below are identical to the no-axes version).
  const m = showAxes ? { left: 40, right: 12, top: 10, bottom: 22 } : { left: 0, right: 0, top: 0, bottom: 0 }
  const chartW = width - m.left - m.right
  const chartH = height - m.top - m.bottom
  const { yMin, yMax } = getAwcChartDomain(values)
  const span = yMax - yMin || 1
  const yOf = (awc: number): number => m.top + chartH * (1 - (clamp(awc, yMin, yMax) - yMin) / span)
  const lastIndex = Math.max(values.length - 1, 1)
  const xOf = (index: number): number => m.left + (index / lastIndex) * chartW
  // Deficit-zone bands by AWC value (boundaries shift with MAD), high → low. Mirrors getSoilDeficitZone:
  // extreme <0, high <MAD×0.3, moderate <MAD, ideal <100, saturated ≥100.
  const moderateDeficit = madValue * (1 - MODERATE_DEFICIT_THRESHOLD)

  const bands: { topAwc: number; bottomAwc: number; zone: TSoilDeficitZone }[] = [
    { topAwc: yMax, bottomAwc: 100, zone: 'saturated' },
    { topAwc: 100, bottomAwc: madValue, zone: 'ideal' },
    { topAwc: madValue, bottomAwc: moderateDeficit, zone: 'moderateDeficit' },
    { topAwc: moderateDeficit, bottomAwc: 0, zone: 'highDeficit' },
    { topAwc: 0, bottomAwc: yMin, zone: 'extremeDeficit' },
  ]

  const d = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xOf(p.index).toFixed(1)} ${yOf(p.value).toFixed(1)}`)
    .join(' ')

  // Thin the x-axis date labels: always the first and last point, plus an even sample in between.
  const labelStep = Math.max(1, Math.ceil(points.length / MAX_X_LABELS))
  const timezone = moment.tz.guess()

  const formatDate = (iso: string | undefined): string =>
    iso ? translate.dates.format(moment.tz(iso, timezone), 'MMM D') : ''

  // First label hugs the left edge, last hugs the right, the rest centre on their point.
  const anchorFor = (i: number, count: number): 'start' | 'end' | 'middle' => {
    if (i === 0) return 'start'

    if (i === count - 1) return 'end'

    return 'middle'
  }

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={translate.phrases.banyanApp('Root Zone Avg')}
    >
      {bands.map((band) => {
        const top = clamp(band.topAwc, yMin, yMax)
        const bottom = clamp(band.bottomAwc, yMin, yMax)

        if (top <= bottom) return null // band fully outside the visible range

        const yTop = yOf(top)

        return (
          <rect
            key={band.zone}
            x={m.left}
            y={yTop}
            width={chartW}
            height={yOf(bottom) - yTop}
            fill={soilDeficitZoneColor[band.zone]}
          />
        )
      })}

      {showAxes &&
        Y_TICKS.filter((tick) => tick >= yMin && tick <= yMax).map((tick) => (
          <g key={tick}>
            <line
              x1={m.left}
              x2={m.left + chartW}
              y1={yOf(tick)}
              y2={yOf(tick)}
              stroke={colors.midnight}
              strokeOpacity={0.12}
              strokeWidth={1}
            />
            <text
              x={m.left - 6}
              y={yOf(tick) + 3}
              textAnchor="end"
              css={{ fontSize: 10, fill: colors.grey800 }}
            >
              {`${tick}%`}
            </text>
          </g>
        ))}

      {showAxes &&
        points
          .filter((p, i) => i % labelStep === 0 || i === points.length - 1)
          .map((p, i, shown) => (
            <text
              key={p.index}
              x={xOf(p.index)}
              y={height - 6}
              textAnchor={anchorFor(i, shown.length)}
              css={{ fontSize: 10, fill: colors.grey800 }}
            >
              {formatDate(dates?.[p.index])}
            </text>
          ))}

      {points.length === 1 ? (
        // A single sample has no line segment — render a dot so it's still visible.
        <circle cx={xOf(points[0].index)} cy={yOf(points[0].value)} r={1.75} fill={colors.midnight} />
      ) : (
        <path d={d} fill="none" stroke={colors.midnight} strokeWidth={1.5} strokeLinejoin="round" />
      )}
    </svg>
  )
}
